import { createFileRoute, Link } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { useState } from "react";
import { Layout } from "@/components/Layout";
import { SEOHead } from "@/components/SEOHead";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { useTaxRates } from "@/hooks/use-query-hooks";
import { formatCurrency } from "@/lib/format";
import { Calculator, Loader2 } from "lucide-react";

export const Route = createFileRoute("/tools/tax-calculator/")({
  head: () => buildSeo({
    title: "Tax Calculator — Lottery Tools | USA Lottery Club",
    description: "Estimate your take-home amount after federal and state taxes on lottery winnings.",
    path: "/tools/tax-calculator",
  }),
  component: TaxCalculator,
});

function TaxCalculator() {
  const { data: taxRates, isLoading } = useTaxRates();
  const [selectedState, setSelectedState] = useState("");
  const [jackpotStr, setJackpotStr] = useState("");
  const [isLumpSum, setIsLumpSum] = useState(true);
  const [result, setResult] = useState<{ gross: number; federal: number; state: number; net: number } | null>(null);

  const taxRate = taxRates?.find((t) => t.states?.slug === selectedState);

  function calculate() {
    const jackpot = parseFloat(jackpotStr.replace(/[,$]/g, ""));
    if (isNaN(jackpot) || !taxRate) return;
    const gross = isLumpSum ? jackpot * 0.6 : jackpot;
    const federal = gross * (taxRate.federal_tax_rate / 100);
    const stateTax = gross * (Number(taxRate.state_tax_rate) / 100);
    setResult({ gross, federal, state: stateTax, net: gross - federal - stateTax });
  }

  return (
    <Layout>
      <SEOHead title="Tax Calculator — Lottery Tools" description="Estimate your take-home after taxes on lottery winnings." />
      <div className="container py-6 md:py-10 max-w-2xl">
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/tools">Tools</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage>Tax Calculator</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <h1 className="font-display text-2xl md:text-3xl font-bold mb-6">
          <Calculator className="inline h-6 w-6 mr-2 text-primary" /> Lottery Tax Calculator
        </h1>
        {isLoading ? <Loader2 className="h-6 w-6 animate-spin text-primary" /> : (
          <Card className="mb-6">
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label htmlFor="tax-state">State</Label>
                <Select value={selectedState} onValueChange={setSelectedState}>
                  <SelectTrigger id="tax-state" aria-label="Select state"><SelectValue placeholder="Select state..." /></SelectTrigger>
                  <SelectContent>
                    {taxRates?.filter((t) => t.states?.slug).map((t) => (
                      <SelectItem key={t.id} value={t.states!.slug}>{t.states!.name} ({Number(t.state_tax_rate)}%)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="tax-jackpot">Jackpot Amount ($)</Label>
                <Input id="tax-jackpot" aria-label="Jackpot amount in dollars" value={jackpotStr} onChange={(e) => setJackpotStr(e.target.value)} inputMode="numeric" placeholder="e.g. 500000000" />
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  id="payout-type"
                  aria-label="Toggle lump sum or annuity"
                  checked={isLumpSum}
                  onCheckedChange={setIsLumpSum}
                />
                <Label htmlFor="payout-type">
                  {isLumpSum ? "Lump Sum (≈60% of advertised)" : "Annuity (full amount)"}
                </Label>
              </div>
              <Button onClick={calculate} disabled={!selectedState || !jackpotStr.trim()} className="w-full">
                <Calculator className="h-4 w-4 mr-2" /> Calculate
              </Button>
            </CardContent>
          </Card>
        )}
        {result && taxRate && (
          <Card className="mb-6">
            <CardContent className="pt-6 space-y-3">
              <div className="flex justify-between border-b py-2"><span className="text-muted-foreground">{isLumpSum ? "Lump Sum" : "Annuity"}</span><span className="font-bold">{formatCurrency(result.gross)}</span></div>
              <div className="flex justify-between border-b py-2"><span className="text-muted-foreground">Federal Tax ({taxRate.federal_tax_rate}%)</span><span className="text-destructive font-bold">−{formatCurrency(result.federal)}</span></div>
              <div className="flex justify-between border-b py-2"><span className="text-muted-foreground">State Tax ({Number(taxRate.state_tax_rate)}%)</span><span className="text-destructive font-bold">−{formatCurrency(result.state)}</span></div>
              <div className="flex justify-between py-2"><span className="font-display font-bold text-lg">Take-Home</span><span className="font-display font-bold text-lg text-success">{formatCurrency(result.net)}</span></div>
            </CardContent>
          </Card>
        )}
        {taxRates && taxRates.length > 0 && (
          <Card>
            <CardContent className="pt-6">
              <h2 className="font-display font-bold text-lg mb-4">Calculate by State</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2">
                {taxRates.filter((t) => t.states?.slug).map((t) => (
                  <Link key={t.id} to="/tools/tax-calculator/$stateSlug" params={{ stateSlug: t.states!.slug }} className="text-sm text-primary hover:underline">
                    {t.states!.name} ({Number(t.state_tax_rate)}%)
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}